import { Box, Card, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router";

import type { SxProps, Theme } from "@mui/material";
import type { FC, ReactNode } from "react";

interface RecentlyEditedRowProps {
  /** What fills the square on the left: a channel icon, a thumbnail. */
  media: ReactNode;
  title: string;
  href: string;
  /** The line under the title, of `RowDetail`s. */
  details: ReactNode;
  pill?: ReactNode;
  /** A button at the right-hand end, after the pill. */
  action?: ReactNode;
  analyticsId: string;
}

/**
 * One entry in the Recently edited feed: its media, its title as a link, and what it is.
 * On a phone the pill and action drop under the details, so the title gets the width.
 */
export const RecentlyEditedRow: FC<RecentlyEditedRowProps> = ({
  media,
  title,
  href,
  details,
  pill,
  action,
  analyticsId,
}) => (
  <Card
    elevation={5}
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "58px minmax(0, 1fr)", sm: "58px minmax(0, 1fr) auto" },
      gridTemplateAreas: { xs: '"media body" "media trailing"', sm: '"media body trailing"' },
      minHeight: 58,
    }}
  >
    <Box
      sx={{
        gridArea: "media",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRight: 1,
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {media}
    </Box>
    <Box sx={{ gridArea: "body", px: 2, py: 0.75 }}>
      <Link
        component={RouterLink}
        to={href}
        variant="subtitle2"
        color="textPrimary"
        underline="hover"
        data-analytics-id={analyticsId}
        sx={{
          display: "block",
          overflow: "hidden",
          overflowWrap: "anywhere",
          textOverflow: "ellipsis",
          whiteSpace: { xs: "normal", sm: "nowrap" },
        }}
      >
        {title}
      </Link>
      <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 3, rowGap: 0.5, mt: 0.5 }}>
        {details}
      </Box>
    </Box>
    {(pill || action) && (
      <Box
        sx={{
          gridArea: "trailing",
          display: "flex",
          alignItems: "center",
          gap: 1,
          pl: { xs: 2, sm: 0 },
          pr: 2,
          pb: { xs: 1, sm: 0 },
          mt: { xs: -1, sm: 0 },
        }}
      >
        {pill}
        {action}
      </Box>
    )}
  </Card>
);

/** One fact under a row's title, with its icon. */
export const RowDetail: FC<{ icon?: ReactNode; children: ReactNode; sx?: SxProps<Theme> }> = ({
  icon,
  children,
  sx,
}) => (
  <Typography
    component="span"
    variant="caption"
    sx={[
      {
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        color: "text.secondary",
        "& > .MuiSvgIcon-root": { fontSize: 16 },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {icon}
    {children}
  </Typography>
);
